import { getValidToken, clearAuthData } from '../utils/auth';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Helper function to get auth token
const getAuthToken = (): string => {
  const token = getValidToken();
  if (!token) {
    throw new Error('No authentication token found. Please login again.');
  }
  return token;
};

// Helper function to handle auth errors
const handleAuthError = (error: any) => {
  if (error.message?.includes('401') || error.message?.includes('authentication') || error.message?.includes('token')) {
    // Clear invalid token
    clearAuthData();
    // You might want to dispatch a logout action here if you have access to the store
    throw new Error('Session expired. Please login again.');
  }
  throw error;
};

export const api = {
  async request(endpoint: string, options: RequestInit = {}, retries = 2) {
    const url = `${API_BASE_URL}${endpoint}`;
    
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        const response = await fetch(url, {
          ...options,
          headers: {
            'Content-Type': 'application/json',
            ...options.headers,
          },
        });
      
      if (!response.ok) {
        // Try to get error message and code from response
        try {
          const errorData = await response.json();
          const error: any = new Error(errorData.error || `HTTP error! status: ${response.status}`);
          // Preserve the error code if available
          if (errorData.code) {
            error.code = errorData.code;
          }
          error.status = response.status;
          throw error;
        } catch (jsonError) {
          // If JSON parsing fails, throw a basic error
          if (jsonError instanceof Error && jsonError.message && jsonError.message !== `HTTP error! status: ${response.status}`) {
            throw jsonError;
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }
      }
      
        return response.json();
      } catch (error) {
        if (attempt === retries) {
          handleAuthError(error);
          throw error;
        }
        // Wait before retry (exponential backoff)
        await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
      }
    }
  },

  // Game-specific methods
  games: {
    async createCoinflip(data: { amount: number; side: string; clientSeed: string }) {
      const token = getAuthToken();
      return api.request('/api/games/coinflip/create', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(data)
      });
    },

    async joinCoinflip(data: { gameId: string; clientSeed?: string }) {
      const token = getAuthToken();
      return api.request('/api/games/coinflip/join', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(data)
      });
    },

    async callBot(gameId: string) {
      const token = getAuthToken();
      return api.request('/api/games/coinflip/call-bot', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ gameId })
      });
    },

    async getWaitingGames() {
      return api.request('/api/games/coinflip/waiting');
    },

    async getGameDetails(gameId: string) {
      return api.request(`/api/games/coinflip/${gameId}`);
    }
  },

  // Authentication methods
  auth: {
    async verify() {
      const token = getAuthToken();
      return api.request('/api/auth/verify', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
    },

    async login(credentials: { email: string; password: string }) {
      return api.request('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify(credentials)
      });
    },

    async register(userData: { username: string; email: string; password: string }) {
      return api.request('/api/auth/signup', {
        method: 'POST',
        body: JSON.stringify(userData)
      });
    }
  }
};

export default api;